import {
    CLEAR_CONTROLS_VALUE,
    CLEAR_VALIDATION_ERRORS,
    CONTROLS_VALUE_CHANGED,
    INIT_CONTROLS_VALUE,
    SET_VALIDATION_DESCRIPTIONS,
    VALIDATE,
} from "./constants";
import {
    ControlReduxRawValue,
    ControlsKey,
    ControlsValueInstantValidationMode,
    IClearControlsValueAction,
    IClearValidationErrorsAction,
    IControlsValueChangedAction,
    IInitControlsValueAction,
    ISetValidationDescriptionsAction,
    IValidateAction,
} from "./types";
import { IExplicitValidationFnDescription, IInstantValidationFnDescription } from "./validation";

/**
 * Inicializuje pole se zadaným klíčem a hodnotou.
 * (Validační popisy (instantní i explicitní) pro daný klíč nastaví na prázdné pole.)
 *
 * -
 *
 * Pozn. K září 2020 se vždy volá s `value = null`.
 * - Akce tak slouží pouze k vytvoření záznamu v Reduxu s daným `controlsKey`.
 * - Obsah tohoto záznamu zatím není znám. Musí být donastaven později. Např. přes {@link createControlsValueChangedAction}.
 *
 * @param controlsKey klíč kontrolu sloužící k jeho jednoznačné identifikaci.
 * @param rawValue počáteční hodnota pole. (Vždy se 1:1 uloží do Reduxu. Žádné validace se neprovádí.)
 */
export const createInitControlsValueAction = (
    controlsKey: ControlsKey,
    rawValue: ControlReduxRawValue,
): IInitControlsValueAction => ({
    type: INIT_CONTROLS_VALUE,
    payload: {
        rawValue,
    },
    meta: {
        controlsKey,
    },
});

/**
 * Nastaví popisy validací do existující instance `controlsKey`.
 *
 * @param controlsKey klíč kontrolu sloužící k jeho jednoznačné identifikaci.
 * @param instantValidationFnDescriptions
 * @param explicitValidationFnDescriptions
 */
export const createSetValidationDescriptionsAction = (
    controlsKey: ControlsKey,
    instantValidationFnDescriptions: ReadonlyArray<IInstantValidationFnDescription>,
    explicitValidationFnDescriptions: ReadonlyArray<IExplicitValidationFnDescription>,
): ISetValidationDescriptionsAction => ({
    type: SET_VALIDATION_DESCRIPTIONS,
    payload: {
        instantValidationFnDescriptions,
        explicitValidationFnDescriptions,
    },
    meta: {
        controlsKey,
    },
});

/**
 * Akce signalizující změnu hodnoty pole.
 *
 * @param controlsKey klíč pole sloužící k jeho jednoznačné identifikaci.
 * @param rawValue nová hodnota pole.
 * @param validationMode Viz {@link ControlsValueInstantValidationMode}. Výchozí hodnota je `userEdit`.
 */
export const createControlsValueChangedAction = (
    controlsKey: ControlsKey,
    rawValue: ControlReduxRawValue,
    validationMode: ControlsValueInstantValidationMode = ControlsValueInstantValidationMode.userEdit,
): IControlsValueChangedAction => ({
    type: CONTROLS_VALUE_CHANGED,
    payload: {
        rawValue,
        validationMode,
    },
    meta: {
        controlsKey,
    },
});

/**
 * Odstraní data pro pole se zadaným klíčem.
 *
 * @param controlsKey Klíč kontrolu k jeho jednoznačné identifikaci.
 */
export const createClearControlsValueAction = (
    controlsKey: ControlsKey,
): IClearControlsValueAction => ({
    type: CLEAR_CONTROLS_VALUE,
    meta: {
        controlsKey,
    },
});

/**
 * Spustí validaci pro pole se zadaným klíčem.
 *
 * @param controlsKey Klíč kontrolu k jeho jednoznačné identifikaci.
 */
export const createValidateAction = (
    controlsKey: ControlsKey,
): IValidateAction => ({
    type: VALIDATE,
    meta: {
        controlsKey,
    },
});

/**
 * Vyprázdní validační chyby ve stavu pro pole se zadaným klíčem.
 *
 * @param controlsKey Klíč kontrolu k jeho jednoznačné identifikaci.
 */
export const createClearValidationErrorsAction = (
    controlsKey: ControlsKey,
): IClearValidationErrorsAction => ({
    type: CLEAR_VALIDATION_ERRORS,
    meta: {
        controlsKey,
    },
});
